<script setup lang="ts">
/**
 * Aura Incorporation — Form 45A: Consent to Act as Director WITH LEAVE OF COURT /
 * written consent of Official Assignee (Companies Act s.173C(a)). 원문 전문 재현.
 *
 * 법원 허가 특수 케이스라 기본 생성되지 않고 툴바 "+ Form 45A" 로 추가한다.
 * 인물은 아래 **Import Director** 로 현재 불러온 T-Link 엔티티의 이사 중에서 골라 채운다.
 */
import { computed, inject, ref } from 'vue';
import CddVarField from './cdd/CddVarField.vue';
import { CDD_FILL, DOC_SCOPE } from './cdd/useCddFill';
import './allotDoc.css';

const fill = inject(CDD_FILL, null);
const scope = inject(DOC_SCOPE, '');

function str(v: unknown): string {
  return v == null ? '' : String(v).trim();
}

interface DirectorOption {
  idx: number;
  name: string;
  identificationNo: string;
  nationality: string;
  address: string;
}

/** T-Link 매핑으로 들어온 이사 목록(dir{N}_director_*) */
const directors = computed<DirectorOption[]>(() => {
  const values = fill?.values || {};
  const out: DirectorOption[] = [];
  for (const k of Object.keys(values)) {
    const m = /^dir(\d+)_director_name$/.exec(k);
    if (!m) continue;
    const name = str(values[k]);
    if (!name) continue;
    const i = Number(m[1]);
    out.push({
      idx: i,
      name,
      identificationNo: str(values[`dir${i}_director_identification_no`]),
      nationality: str(values[`dir${i}_director_nationality`]),
      address: str(values[`dir${i}_director_address`]),
    });
  }
  return out.sort((a, b) => a.idx - b.idx);
});

const picked = ref<number | null>(null);
const notice = ref('');

/** 선택한 이사를 이 문서(스코프)의 Particulars of Director 로 복사 */
function importDirector() {
  if (!fill || picked.value == null) return;
  const d = directors.value.find((x) => x.idx === picked.value);
  if (!d) return;
  const set = (key: string, val: string) => {
    if (val) fill.setValue(scope + key, val);
  };
  set('director_name', d.name);
  set('director_identification_no', d.identificationNo);
  set('director_nationality', d.nationality);
  set('director_residential_address', d.address);
  // 서명란은 기존 SG 관례대로 이름 텍스트
  set('director_signature', d.name);
  notice.value = `Imported: ${d.name}`;
  window.setTimeout(() => { notice.value = ''; }, 2500);
}
</script>

<template>
  <div class="allot-doc">
    <div class="doc-head" style="text-align:center">
      <div class="doc-company" style="letter-spacing:.25em">FORM 45A</div>
      <div class="doc-meta" style="margin-top:6px">THE COMPANIES ACT (CHAPTER 50) SECTION 173C(a)</div>
      <div class="doc-lbl-title" style="margin-top:6px">
        CONSENT TO ACT AS DIRECTOR AND STATEMENT OF NON DISQUALIFICATION TO ACT AS DIRECTOR
        WITH LEAVE OF COURT OR/AND WRITTEN CONSENT OF OFFICIAL ASSIGNEE
      </div>
    </div>

    <table class="cdd-tbl">
      <tbody>
        <tr><td class="cdd-lcell">Name of Company</td><td><CddVarField field-key="company_name" label="Company Name" /></td></tr>
        <tr><td class="cdd-lcell">Company No</td><td><CddVarField field-key="company_registration_no" label="Company Registration No." /></td></tr>
      </tbody>
    </table>

    <p class="doc-p" style="margin-top:10px">
      I, the undermentioned person, hereby consent to act as a director of the abovenamed company with effect from
      <CddVarField field-key="consent_effective_date" label="Consent Effective Date" /> (date) and declare that:
    </p>
    <p class="doc-p in">(a) I am not below 18 years of age and that I am otherwise of full legal capacity. <i>[For Directors Acting with Leave of Court, please complete items (b) to (d).]</i></p>
    <p class="doc-p in">*(b) I have been granted leave by the General Division of the High Court to be a director of the above-named company.</p>
    <p class="doc-p in">*(c) The Order of Court granting me leave was made on <CddVarField field-key="court_leave_date" label="Court Leave Date" /> (date).</p>
    <p class="doc-p in">*(d) The Court Reference No of the matter is <CddVarField field-key="court_reference_no" label="Court Reference No" /></p>
    <p class="doc-p"><i>[For Directors Acting with written permission of Official Assignee, please complete items (e) and (f).]</i></p>
    <p class="doc-p in">I have been granted written permission by the Official Assignee.</p>
    <p class="doc-p in">The written permission was granted by the Official Assignee on <CddVarField field-key="official_assignee_permission_date" label="Official Assignee Permission Date" /> (dd/mm/yyyy) and includes the following conditions:</p>
    <p class="doc-p in2">— I will not incur personal debts and/liability in the course of managing the company, including being a guarantor for loans granted by the company.</p>
    <p class="doc-p in2">— I will submit to the Official Assignee a yearly audited Statement of Accounts of the company.</p>
    <p class="doc-p in2">— I shall only sign cheques on behalf of the company together with a director of the company.</p>
    <p class="doc-p in2">— In the event that the company is seeking to be listed on any securities/stock exchange, I must inform the Official Assignee of the same.</p>

    <p class="doc-p">I am aware of and undertake to abide by my duties, responsibilities and liabilities specified in the Companies Act ("the Act") as well as under common law where applicable, including the following key administrative and substantive duties, that is, to:</p>
    <p class="doc-p in2">— discharge my responsibilities in the company;</p>
    <p class="doc-p in2">— ensure that I have a reasonable degree of skill and knowledge to handle the affairs of the company;</p>
    <p class="doc-p in2">— act honestly and be reasonably diligent in discharging my duties and act in the interest of the company without putting myself in a position of conflict of interest;</p>
    <p class="doc-p in2">— employ the powers and assets that I am entrusted with for the proper purposes of the company and not for any collateral purposes;</p>
    <p class="doc-p in2">— ensure that the company and I comply with all the requirements and obligations under the Act including those in respect of meetings, requisitions, resolutions, accounts, reports, statements, records and other documents on the company, filing and notices and any other prerequisites; and</p>
    <p class="doc-p in2">— account to the shareholders for my conduct of the affairs of the company and make such disclosures that are incumbent upon me under the Act.</p>

    <p class="doc-p">By virtue of the foregoing I am not disqualified from acting as a director of the abovenamed company and that —</p>
    <p class="doc-p in">*(a) I have read and understood the above statements; or</p>
    <p class="doc-p in">*(b) the above statements were interpreted to me in ………………………………………… (state language/dialect) by ……………………………………… (state name) NRIC No: …………………………………… before I executed this form and I confirm that the statements are true. I am also aware that I can be prosecuted in Court if I willfully give any information on this form which is false.</p>

    <p class="doc-sec-title" style="text-align:left">Particulars of Director</p>

    <!-- T-Link 엔티티의 이사 불러오기 (문서 본문 아님 — 입력 보조 도구) -->
    <div class="f45a-import no-print">
      <span class="f45a-import-lbl">Import Director</span>
      <template v-if="directors.length">
        <select v-model="picked" class="f45a-select">
          <option :value="null" disabled>Select a director from the linked entity…</option>
          <option v-for="d in directors" :key="d.idx" :value="d.idx">
            {{ d.name }}{{ d.identificationNo ? ` (${d.identificationNo})` : '' }}
          </option>
        </select>
        <button type="button" class="f45a-btn" :disabled="picked === null" @click="importDirector">
          Import
        </button>
        <span v-if="notice" class="f45a-ok">{{ notice }}</span>
      </template>
      <span v-else class="f45a-empty">
        No directors loaded — link a T-Link incorporation first, or type the details below.
      </span>
    </div>

    <table class="cdd-tbl">
      <tbody>
        <tr><td class="cdd-lcell">Name</td><td><CddVarField field-key="director_name" label="Director Name" /></td></tr>
        <tr><td class="cdd-lcell">Address</td><td><CddVarField field-key="director_residential_address" label="Director Residential Address" /></td></tr>
        <tr><td class="cdd-lcell">*NRIC/Passport No.</td><td><CddVarField field-key="director_identification_no" label="Director Identification No." /></td></tr>
        <tr><td class="cdd-lcell">Nationality</td><td><CddVarField field-key="director_nationality" label="Director Nationality" /></td></tr>
        <tr><td class="cdd-lcell">Signature</td><td><CddVarField field-key="director_signature" label="Director Signature" /></td></tr>
      </tbody>
    </table>
    <p class="doc-p">Dated this <CddVarField field-key="dated_day" label="Dated Day" /> day of <CddVarField field-key="dated_month" label="Dated Month" />, <CddVarField field-key="dated_year" label="Dated Year" /></p>
    <p class="doc-note">* Delete where inapplicable.</p>
  </div>
</template>

<style scoped>
.f45a-import {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin: 6px 0 10px;
  padding: 8px 10px;
  border: 1px dashed #b9d0ff;
  border-radius: 8px;
  background: #f5f9ff;
}
.f45a-import-lbl { font-size: 11px; font-weight: 800; color: #1b64da; }
.f45a-select {
  flex: 1 1 260px;
  min-width: 0;
  font-size: 11.5px;
  padding: 5px 7px;
  border: 1px solid #d3dceb;
  border-radius: 6px;
  background: #fff;
  color: #1f2d4d;
}
.f45a-btn {
  border: 1px solid #1b84ff;
  background: #fff;
  color: #1b84ff;
  border-radius: 999px;
  padding: 5px 13px;
  font-size: 11.5px;
  font-weight: 700;
  cursor: pointer;
}
.f45a-btn:hover:not(:disabled) { background: #eaf3ff; }
.f45a-btn:disabled { opacity: .45; cursor: default; }
.f45a-ok { font-size: 11px; font-weight: 700; color: #22a06b; }
.f45a-empty { font-size: 11px; color: #7a869c; }

.doc-p.in { margin-left: 18px; }
.doc-p.in2 { margin-left: 34px; }
.doc-note { font-size: 10px; color: #555; font-style: italic; margin-top: 8px; }
</style>
